'use client';
import React, { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { ExclamationTriangleIcon, BellIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeRefresh } from '@/hooks/useRealtimeRefresh';
import { SkeletonCard, SkeletonTable, SkeletonChart } from '@/components/ui/Skeleton';
import { useRetryFetch } from '@/hooks/useRetryFetch';
import ErrorAlert from '@/components/ui/ErrorAlert';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { computeStudentCompleteness, getCompletenessBadge } from '@/lib/dataValidation';
import StudentTimetableTab from '@/app/student-portal/components/StudentTimetableTab';
import StudentCourseResourcesTab from '@/app/student-portal/components/StudentCourseResourcesTab';
import { usePreferences } from '@/contexts/PreferencesContext';
import Link from 'next/link';
import {
  BanknotesIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';

const AttendanceChart = dynamic(() => import('./StudentAttendanceChart'), { ssr: false });

interface StudentRecord {
  id: string;
  student_number: string;
  full_name: string;
  gpa: number;
  completed_units: number;
  total_units: number;
  enrollment_status: string;
  fee_status: string;
  fee_outstanding: number;
  year_of_study: number;
  semester: string;
  programs: { name: string; pngqf_level: string } | null;
  campuses: { name: string } | null;
}

interface EnrollmentRecord {
  id: string;
  attendance_pct: number;
  enrollment_status: string;
  courses: {
    id: string;
    code: string;
    name: string;
    room: string;
    schedule: string;
    credit_hours: number;
    trainer_id: string | null;
  } | null;
}

interface GradeRecord {
  id: string;
  assessment_name: string;
  score: number | null;
  grade: string | null;
  max_score: number;
  feedback: string;
  assessed_date: string | null;
  courses: { code: string } | null;
}

function GradeBadge({ grade }: { grade: string | null }) {
  if (!grade) return <span className="text-xs text-muted-foreground italic">Pending</span>;
  const cls: Record<string, string> = {
    HD: 'grade-hd', D: 'grade-d', C: 'grade-c', P: 'grade-p', F: 'grade-f',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-700 ${cls[grade] || 'grade-c'}`}>
      {grade}
    </span>
  );
}

function AttendancePill({ pct }: { pct: number }) {
  const color = pct >= 80 ? 'text-positive bg-[var(--positive-bg)]' : pct >= 70 ? 'text-warning bg-[var(--warning-bg)]' : 'text-negative bg-[var(--negative-bg)]';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-600 ${color}`}>
      {pct}%
    </span>
  );
}

// ─── Quick Access Cards ───────────────────────────────────────────────────────

function QuickAccessCards({ feeOutstanding, feeStatus }: { feeOutstanding: number; feeStatus: string }) {
  const cards = [
    {
      href: '/student-portal/fees',
      icon: <BanknotesIcon className="w-5 h-5 text-white" />,
      label: 'Fee Statement',
      sub: feeStatus === 'cleared' ? 'All fees cleared' : `PGK ${feeOutstanding.toLocaleString()} outstanding`,
      bg: feeStatus === 'cleared' ? 'bg-positive' : 'bg-warning',
    },
    {
      href: '/student-portal/transcript',
      icon: <AcademicCapIcon className="w-5 h-5 text-white" />,
      label: 'Academic Transcript',
      sub: 'View grades & GPA',
      bg: 'bg-primary',
    },
    {
      href: '/student-portal/clearance',
      icon: <ShieldCheckIcon className="w-5 h-5 text-white" />,
      label: 'Registration Clearance',
      sub: 'Track clearance steps',
      bg: 'bg-indigo-600',
    },
    {
      href: '/student-portal/documents',
      icon: <FolderOpenIcon className="w-5 h-5 text-white" />,
      label: 'My Documents',
      sub: 'Download certificates',
      bg: 'bg-purple-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map(card => (
        <Link
          key={card.href}
          href={card.href}
          className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl card-shadow hover:border-primary/30 hover:shadow-md transition-all group"
        >
          <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
            {card.icon}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-700 text-foreground truncate">{card.label}</p>
            <p className="text-xs text-muted-foreground truncate">{card.sub}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function StudentDashboardContent() {
  const { prefs, fmt } = usePreferences();
  const [activeTab, setActiveTab] = useState<'courses' | 'results' | 'timetable' | 'resources'>('courses');
  const [student, setStudent] = useState<StudentRecord | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentRecord[]>([]);
  const [grades, setGrades] = useState<GradeRecord[]>([]);

  const doFetch = useCallback(async () => {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: studentData, error: studentError } = await supabase
      .from('students')
      .select('*, programs(name, pngqf_level), campuses(name)')
      .eq('user_id', user.id)
      .maybeSingle();

    if (studentError) throw new Error(studentError.message);
    if (!studentData) throw new Error('No student record found.');
    setStudent(studentData);

    const { data: enrollData, error: enrollError } = await supabase
      .from('enrollments')
      .select('id, attendance_pct, enrollment_status, courses(id, code, name, room, schedule, credit_hours, trainer_id)')
      .eq('student_id', studentData.id)
      .neq('enrollment_status', 'withdrawn');

    if (!enrollError) setEnrollments(enrollData || []);

    const { data: gradeData, error: gradeError } = await supabase
      .from('grades')
      .select('id, assessment_name, score, grade, max_score, feedback, assessed_date, courses(code)')
      .eq('student_id', studentData.id)
      .eq('is_submitted', true)
      .order('assessed_date', { ascending: false })
      .limit(10);

    if (!gradeError) setGrades(gradeData || []);
  }, []);

  const { loading, error, retryCount, retrying, execute } = useRetryFetch(doFetch, {
    maxRetries: 3,
    auditOnFailure: true,
    auditResourceType: 'student_dashboard',
  });

  useEffect(() => { execute(); }, [execute]);

  // Live updates: re-fetch when attendance, grades, or enrollments change
  useRealtimeRefresh(['attendance', 'grades', 'enrollments', 'students'], execute);

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Banner skeleton */}
        <div className="bg-primary/20 rounded-2xl p-6 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/30" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 bg-primary/30 rounded" />
              <div className="h-6 w-48 bg-primary/30 rounded" />
              <div className="h-3 w-64 bg-primary/30 rounded" />
            </div>
          </div>
        </div>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
        {/* Chart + table */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-card rounded-2xl card-shadow border border-border p-5">
            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-4" />
            <SkeletonTable rows={4} cols={4} />
          </div>
          <div className="bg-card rounded-2xl card-shadow border border-border p-5">
            <div className="h-4 w-28 bg-gray-200 rounded animate-pulse mb-4" />
            <SkeletonChart height="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (error || (!loading && !student)) {
    return (
      <ErrorAlert
        message={error || 'Student record not found.'}
        context="student"
        onRetry={execute}
        retrying={retrying}
        retryCount={retryCount}
      />
    );
  }

  const overallAttendance = enrollments.length
    ? Math.round(enrollments.reduce((s, e) => s + (e.attendance_pct || 0), 0) / enrollments.length)
    : 0;

  const feeCleared = student.fee_status === 'cleared';
  const initials = student.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const profileCompleteness = computeStudentCompleteness(student as unknown as Record<string, unknown>);
  const profileBadge = getCompletenessBadge(profileCompleteness.level);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Banner */}
      <div className="bg-primary rounded-2xl p-6 text-white flex flex-col md:flex-row md:items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-700 flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1">
          <p className="text-white/70 text-sm font-500">Welcome back,</p>
          <h2 className="text-2xl font-700">{student.full_name}</h2>
          <p className="text-white/80 text-sm mt-0.5">
            {student.programs?.name} · {student.programs?.pngqf_level} · {student.campuses?.name}
          </p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-1">
          <span className="bg-white/20 text-white text-xs font-600 px-3 py-1 rounded-full">{student.semester}</span>
          <span className="text-white/70 text-xs">Student ID: {student.student_number}</span>
          <span
            title={profileCompleteness.missingFields.length > 0 ? `Missing: ${profileCompleteness.missingFields.join(', ')}` : 'Profile complete'}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-600 border ${profileBadge.bgClass} ${profileBadge.colorClass} ${profileBadge.borderClass}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${profileBadge.dotClass}`} />
            Profile {profileBadge.label} · {profileCompleteness.score}%
          </span>
        </div>
      </div>

      {/* Fee Alert */}
      {!feeCleared && (
        <div className="bg-[var(--warning-bg)] border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-600 text-warning">Fee Payment Reminder</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Outstanding balance: PGK {student.fee_outstanding.toLocaleString()}. Unpaid fees may affect enrolment for Semester 2 and graduation clearance.
            </p>
          </div>
          <Link href="/student-portal/fees" className="text-xs font-600 text-warning bg-amber-100 hover:bg-amber-200 transition-colors px-3 py-1.5 rounded-lg">
            View Statement
          </Link>
        </div>
      )}

      {/* Quick Access */}
      <QuickAccessCards feeOutstanding={student.fee_outstanding} feeStatus={student.fee_status} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 card-shadow border border-border">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Enrollment Status</p>
          <div className="flex items-center gap-2">
            <CheckSolid className="w-5 h-5 text-positive" />
            <span className="text-lg font-700 text-foreground capitalize">{student.enrollment_status}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Year {student.year_of_study} · {student.semester}</p>
        </div>
        <div className="bg-card rounded-xl p-4 card-shadow border border-border">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Overall Attendance</p>
          <p className={`text-3xl font-700 font-tabular ${overallAttendance >= 80 ? 'text-positive' : 'text-warning'}`}>{overallAttendance}%</p>
          <p className="text-xs text-muted-foreground mt-1">Across {enrollments.length} courses</p>
        </div>
        <div className="bg-card rounded-xl p-4 card-shadow border border-border">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Units Progress</p>
          <p className="text-3xl font-700 font-tabular text-foreground">
            {student.completed_units}<span className="text-base text-muted-foreground font-500">/{student.total_units}</span>
          </p>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${(student.completed_units / student.total_units) * 100}%` }} />
          </div>
        </div>
        <div className="bg-card rounded-xl p-4 card-shadow border border-border">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Current GPA</p>
          <p className="text-3xl font-700 font-tabular text-primary">{student.gpa.toFixed(1)}</p>
          <p className="text-xs text-muted-foreground mt-1">Distinction average</p>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left — Tabs */}
        <div className="xl:col-span-2 bg-card rounded-2xl card-shadow border border-border overflow-hidden">
          <div className="flex border-b border-border overflow-x-auto">
            {(['courses', 'results', ...(prefs.dashboard_widgets.upcoming_schedule ? ['timetable' as const] : []), 'resources'] as const).map((tab) => (
              <button
                key={`tab-${tab}`}
                onClick={() => setActiveTab(tab as typeof activeTab)}
                className={`px-5 py-3.5 text-sm font-600 transition-colors border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'courses' ? 'My Courses' : tab === 'results' ? 'Recent Results' : tab === 'timetable' ? 'This Week' : 'Course Resources'}
              </button>
            ))}
          </div>

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div className="divide-y divide-border">
              {enrollments.length === 0 && (
                <p className="px-5 py-8 text-center text-muted-foreground text-sm">No enrolled courses found.</p>
              )}
              {enrollments.map((enr) => {
                const course = enr.courses;
                if (!course) return null;
                const isAtRisk = enr.enrollment_status === 'at_risk';
                return (
                  <div key={enr.id} className={`px-5 hover:bg-muted/30 transition-colors ${prefs.display_compact_tables ? 'py-2.5' : 'py-4'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-700 text-primary bg-secondary px-2 py-0.5 rounded">{course.code}</span>
                          {isAtRisk && (
                            <span className="text-xs font-600 text-negative bg-[var(--negative-bg)] px-2 py-0.5 rounded">At Risk</span>
                          )}
                        </div>
                        <p className="text-sm font-600 text-foreground mt-1">{course.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{course.credit_hours} credit hours</p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <AttendancePill pct={enr.attendance_pct} />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><ClockIcon className="w-3.5 h-3.5" />{course.schedule}</span>
                      <span className="flex items-center gap-1"><MapPinIcon className="w-3.5 h-3.5" />{course.room}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <div className="divide-y divide-border">
              {grades.length === 0 && (
                <p className="px-5 py-8 text-center text-muted-foreground text-sm">No results published yet.</p>
              )}
              {grades.slice(0, prefs.display_items_per_page).map((r) => (
                <div key={r.id} className={`px-5 hover:bg-muted/30 transition-colors ${prefs.display_compact_tables ? 'py-2.5' : 'py-4'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-700 text-primary bg-secondary px-2 py-0.5 rounded">{r.courses?.code}</span>
                        <span className="text-xs text-muted-foreground">{fmt(r.assessed_date)}</span>
                      </div>
                      <p className="text-sm font-600 text-foreground">{r.assessment_name}</p>
                      {r.feedback && <p className="text-xs text-muted-foreground mt-1 italic">"{r.feedback}"</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <GradeBadge grade={r.grade} />
                      {r.score !== null && (
                        <span className="text-sm font-700 text-foreground font-tabular">
                          {r.score}<span className="text-xs text-muted-foreground font-400">/{r.max_score}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Timetable Tab — only shown when upcoming_schedule widget is enabled */}
          {activeTab === 'timetable' && prefs.dashboard_widgets.upcoming_schedule && (
            <StudentTimetableTab
              studentId={student.id}
              semester={student.semester}
              campusId={student.campuses ? (student as any).campus_id ?? null : null}
            />
          )}

          {/* Course Resources Tab */}
          {activeTab === 'resources' && (
            <StudentCourseResourcesTab studentId={student.id} />
          )}
        </div>

        {/* Right — Attendance Chart + Notifications */}
        <div className="space-y-4">
          {/* Attendance chart — controlled by attendance_summary widget */}
          {prefs.dashboard_widgets.attendance_summary && (
            <div className="bg-card rounded-2xl card-shadow border border-border p-5">
              <p className="text-sm font-700 text-foreground mb-1">Attendance Trend</p>
              <p className="text-xs text-muted-foreground mb-3">Last 8 weeks · All courses</p>
              <ErrorBoundary variant="section" sectionLabel="Attendance Chart">
                <AttendanceChart studentId={student.id} />
              </ErrorBoundary>
            </div>
          )}

          {/* Notifications panel — controlled by recent_notifications widget */}
          {prefs.dashboard_widgets.recent_notifications && (
            <div className="bg-card rounded-2xl card-shadow border border-border overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <p className="text-sm font-700 text-foreground">Notifications</p>
                <BellIcon className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="divide-y divide-border">
                {!feeCleared && (
                  <div className={`px-5 flex items-start gap-3 ${prefs.display_compact_tables ? 'py-2.5' : 'py-3.5'}`}>
                    <div className="w-2 h-2 rounded-full bg-warning mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-600 text-foreground">Fee Payment Reminder</p>
                      <p className="text-xs text-muted-foreground mt-0.5">PGK {student.fee_outstanding.toLocaleString()} outstanding</p>
                    </div>
                  </div>
                )}
                {enrollments.some((e) => e.attendance_pct < 80) && (
                  <div className={`px-5 flex items-start gap-3 ${prefs.display_compact_tables ? 'py-2.5' : 'py-3.5'}`}>
                    <div className="w-2 h-2 rounded-full bg-negative mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-600 text-foreground">Attendance Warning</p>
                      <p className="text-xs text-muted-foreground mt-0.5">One or more courses below 80% — at risk of exclusion</p>
                    </div>
                  </div>
                )}
                {feeCleared && enrollments.every((e) => e.attendance_pct >= 80) && (
                  <div className="px-5 py-3.5 text-center text-sm text-muted-foreground">No new notifications</div>
                )}
              </div>
            </div>
          )}

          {/* If both widgets are off, show a placeholder so the right column isn't empty */}
          {!prefs.dashboard_widgets.attendance_summary && !prefs.dashboard_widgets.recent_notifications && (
            <div className="bg-card rounded-2xl card-shadow border border-border p-5 text-center text-sm text-muted-foreground">
              <p className="font-600 text-foreground mb-1">Widgets hidden</p>
              <p>Enable Attendance Summary or Recent Notifications in <a href="/account-settings/preferences" className="text-primary underline">Preferences</a>.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}